import { requestAttachmentTree } from "../temp/attachments/attachmentsTempActions";
import * as types from "./persistenceActionTypes";
import * as actions from "./persistenceActions";
import { createAction } from "@reduxjs/toolkit";
import * as dates from "common/dates";
import * as logger from "common/logger";
import { hasValue } from "common/strings";
import * as config from "config";
import * as errorActions from "store/actions/error/errorActions";
import * as exposureActions from "store/actions/input/exposure/exposureActions";
import * as hazardActions from "store/actions/input/exposure/hazardActions";
import * as peersActions from "store/actions/peers/peersActions";
import * as maintenanceStatusActions from "store/actions/temp/maintenance/maintenanceStatusActions";
import { setSavedPersistentState } from "store/actions/temp/navigation/navigationActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import { selectPersistedState } from "store/selectors/persistence/persistenceSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as utils from "utils";

export { upgradeState } from "./upgrade";

export const cancelRenewSubmission = () => ({
  type: types.CANCEL_RENEW_SUBMISSION,
});

/**
 * Create a new submission and open it. When `insured` is given (for example
 * the text the user searched for), the backend starts the new submission
 * with that insured name.
 */
export const newSubmission = ({ navigate, insured = null }) =>
  _loadSubmission(
    async () => {
      const response = await utils.authenticatedFetch(
        config.endpoints.persistence("create-submission"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(hasValue(insured) ? { insured } : {}),
        }
      );
      return await response.json();
    },
    {
      onFinalize: () => {
        navigate("/submissions/new");
      },
    }
  );

export const renewSubmission = ({
  navigate,
  force = false,
  submissionId = null,
}) => (dispatch, getState) => {
  if (submissionId) {
    dispatch(
      _loadSubmission(
        async () => {
          const response = await utils.authenticatedFetch(
            config.endpoints.persistence("create-submission"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ renewFromSubmissionId: submissionId }),
            }
          );
          return await response.json();
        },
        {
          onFinalize: () => {
            navigate("/submissions/new");
          },
        }
      )
    );
    return;
  }
  const priorState = getState();
  const renewingInception = new Date(programSelectors.getInception(priorState));
  const renewDirectly = force || renewingInception < dates.getDate();
  if (renewDirectly) {
    const renewFromSubmission = {
      state: selectPersistedState(priorState),
      filename: priorState?.persistence?.filename,
    };

    dispatch(
      _loadSubmission(
        async () => {
          const response = await utils.authenticatedFetch(
            config.endpoints.persistence("create-submission"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ renewFromSubmission }),
            }
          );
          return await response.json();
        },
        {
          onFinalize: () => {
            navigate("/submissions/new");
          },
        }
      )
    );
  } else {
    dispatch({
      type: types.RENEW_SUBMISSION_DIALOG,
      payload: {
        priorState,
      },
    });
  }
};

export const duplicateSubmission = ({ navigate }) => (dispatch, getState) => {
  const state = getState();
  const duplicateFromSubmission = {
    state: selectPersistedState(state),
    filename: state?.persistence?.filename,
  };

  dispatch(
    _loadSubmission(
      async () => {
        const response = await utils.authenticatedFetch(
          config.endpoints.persistence("create-submission"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              duplicateFromSubmission: duplicateFromSubmission,
            }),
          }
        );
        return await response.json();
      },
      {
        onFinalize: () => {
          navigate("/submissions/new");
        },
      }
    )
  );
};

const _loadSubmission = (querySubmission, { onFinalize = null } = {}) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: types.LOAD_SUBMISSION_STARTED });
    const rawData = await querySubmission();
    const data = actions.upgradeState(rawData);
    const schemaVersion = data?.state?.schemaVersion;
    const state = getState();
    if (schemaVersion) {
      if (schemaVersion > config.CURRENT_FILE_SCHEMA_VERSION) {
        logger.error(
          "Loaded file with schema version greater than current schema version",
          {
            breadcrumb: {
              schemaVersion,
              currentSchemaVersion: config.CURRENT_FILE_SCHEMA_VERSION,
              filename: rawData?.filename,
            },
          }
        );
        const featureFlags = staticDataSelectors.selectConfig("feature_flags")(
          state
        );
        const disableSchemaVersionValidation =
          featureFlags?.["ui/disableSchemaVersionValidation"] ?? false;
        if (!disableSchemaVersionValidation) {
          dispatch(
            maintenanceStatusActions.updateMaintenanceStatus({
              forceReload: true,
              dialogTitle: "Please reload the application",
              dialogMessage:
                "Please reload the application. Following an update, this version is no longer compatible.",
            })
          );
          return;
        }
      }
      if (schemaVersion < config.CURRENT_FILE_SCHEMA_VERSION) {
        logger.error(
          "Loaded file with schema version less than current schema version",
          {
            breadcrumb: {
              schemaVersion,
              currentSchemaVersion: config.CURRENT_FILE_SCHEMA_VERSION,
              filename: rawData?.filename,
            },
          }
        );
      }
    }
    dispatch({
      type: types.LOAD_SUBMISSION_SUCCEEDED,
      payload: {
        data,
      },
    });
    dispatch((dispatch, getState) =>
      dispatch(setSavedPersistentState(selectPersistedState(getState())))
    );
    (data?.state?.peers?.submissions || []).forEach((submission) => {
      dispatch(peersActions.loadPeer(submission));
    });
    dispatch(hazardActions.updateHazardProfileSummary());
    if (data?.state?.input?.program?.industryClass) {
      dispatch(
        exposureActions.updateExposureMeasures([
          data.state.input.program.industryClass,
        ])
      );
    }
    if (data?.state?.input?.program?.priorSubmission) {
      dispatch(
        actions.loadPriorSubmission(
          data?.state?.input?.program?.priorSubmission
        )
      );
    }
    const refreshAttachmentTreeMetadat = (dir) => {
      (dir?.attachedFiles || [])
        .filter((fid) => fid)
        .forEach((fileId) => dispatch(requestAttachmentTree(fileId)));
      (dir?.subDirectories || []).forEach((subDir) =>
        refreshAttachmentTreeMetadat(subDir)
      );
    };
    refreshAttachmentTreeMetadat(data?.state?.input?.attachments);
  } catch (e) {
    logger.exception(e);
    dispatch({ type: types.LOAD_SUBMISSION_FAILED });
  } finally {
    onFinalize && onFinalize();
  }
};

export const loadExistingSubmission = (submissionId) =>
  _loadSubmission(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.persistence(
        "get?" + new URLSearchParams({ filename: `${submissionId}.json` })
      )
    );
    return await response.json();
  });

export const loadExistingSubmissionVersion = (submissionId, versionId) =>
  _loadSubmission(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.persistence("retrieve-version"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          submissionId,
          versionId,
        }),
      }
    );
    const submission = await response.json();
    return {
      ...submission,
      state: {
        ...submission?.state,
        meta: {
          ...submission?.state?.meta,
          overlay: {
            type: "FromVersion",
            versionId,
          },
        },
      },
    };
  });

export const loadTransferredSubmission = (transferId) =>
  _loadSubmission(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.transfer("retrieve-merge-definition"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          transferId,
        }),
      }
    );
    const mergeDefinition = await response.json();
    return mergeDefinition?.merged?.submission;
  });

export const loadPriorSubmission = (filename) => (dispatch) => {
  utils
    .authenticatedFetch(
      config.endpoints.persistence("get?" + new URLSearchParams({ filename }))
    )
    .then((result) => result.json())
    .then((rawData) => actions.upgradeState(rawData))
    .then((data) => {
      dispatch({
        type: types.RECEIVED_PRIOR_SUBMISSION,
        payload: data,
      });
    })
    .catch((error) => {
      errorActions.handleError(error, {
        title: "Unable to open Prior Submission",
        message: `Could not load: ${filename}`,
        type: "UNABLE_TO_OPEN_SUBMISSION",
      });
    });
};

export const saveAcceptedTransmission = (
  submissionId,
  submissionTransferId
) => ({
  type: types.SAVE_ACCEPTED_TRANSMISSION,
  payload: { submissionId, submissionTransferId },
});

export const acceptedTransmissionSaved = (submissionId) => ({
  type: types.ACCEPTED_TRANSMISSION_SAVED,
  payload: { submissionId },
});

export const transferCleanup = () => ({
  type: types.ACCEPTED_TRANSMISSION_CLEANUP,
});
export const saveState = {
  requested: createAction("persistence/saveState"),
  accept_transmission: createAction("persistence/acceptTransmission"),
  transmission_accepted: createAction("persistence/acceptTransmission"),
  started: createAction("persistence/saveState/started"),
  succeeded: createAction(
    "persistence/saveState/succeeded",
    ({ savedAt, filename, savedBy, overwritten, savedToken }) => {
      return {
        payload: {
          savedAt,
          filename,
          savedBy,
          overwritten,
          savedToken,
        },
      };
    }
  ),
  failed: createAction("persistence/saveState/failed", (errorMessage) => {
    return {
      payload: errorMessage,
    };
  }),
};
